import React from "react";
import {Box, Chip, InputAdornment, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {formatReais} from "../../../shared/utils/formatters";
import {plural, SITUACOES_DO_MODULO, situacaoDoModulo} from "../planoVersaoUtils";

const celula = {px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', verticalAlign: 'top', fontSize: 14};
const COR = {INCLUIDO: 'primary', OPCIONAL: 'info', FORA: 'default'};

/*
 * Cartão "Acesso" da versão: cada módulo do sistema é incluído, opcional com preço, ou fica fora. No rascunho, um
 * módulo com pendência no catálogo pode ser marcado, mas impede publicar enquanto não sair ou for resolvido
 * (decisão de 18/10/2026). A publicada aparece só para leitura.
 */
export default function AcessoCard({modulos, form, editavel, bloqueios = {}, onSituacao, onPreco}) {
    const incluidos = modulos.filter((modulo) => situacaoDoModulo(form, modulo.id) === 'INCLUIDO').length;

    return (
        <Paper variant="outlined" sx={{p: {xs: 2, md: 2.5}}} component="section" aria-label="Acesso">
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5, gap: 1, flexWrap: 'wrap'}}>
                <Typography variant="h6" component="h2" fontWeight={700}>Acesso</Typography>
                <Typography variant="caption" color="text.secondary">{incluidos} de {modulos.length} módulos incluídos</Typography>
            </Box>
            {!modulos.length ? <Typography variant="body2" color="text.secondary">O sistema ainda não tem módulos.</Typography> : (
                <Box sx={{overflowX: 'auto'}}>
                    <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 360}}>
                        <thead>
                            <Box component="tr" sx={{'& th': {px: 1.5, py: 1, textAlign: 'left', color: 'text.secondary', fontWeight: 500, fontSize: 13}}}>
                                <th>Módulo</th>
                                <th>Situação e preço do opcional</th>
                            </Box>
                        </thead>
                        <tbody>
                            {modulos.map((modulo) => {
                                const situacao = situacaoDoModulo(form, modulo.id);
                                const bloqueio = bloqueios[modulo.id] || [];
                                const preco = form.modulos[modulo.id]?.preco_opcional ?? '';
                                return (
                                    <tr key={modulo.id}>
                                        <Box component="td" sx={celula}>
                                            <Typography variant="body2" fontWeight={600}>{modulo.nome}</Typography>
                                            <Typography variant="caption" color="text.secondary">{plural((modulo.funcionalidades || []).length, 'funcionalidade', 'funcionalidades')}</Typography>
                                            {bloqueio.length ? (
                                                <Tooltip title={bloqueio.join(' ')}>
                                                    <Chip size="small" sx={{mt: 0.5, display: 'flex', width: 'fit-content'}}
                                                          color={situacao === 'FORA' ? 'default' : 'error'}
                                                          label={situacao === 'FORA' ? 'Pendência no catálogo' : 'Impede publicar'} />
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                        <Box component="td" sx={celula}>
                                            {editavel ? (
                                                <ToggleButtonGroup size="small" exclusive value={situacao}
                                                                   onChange={(_, valor) => valor && onSituacao(modulo.id, valor)}
                                                                   aria-label={`Situação de ${modulo.nome}`}>
                                                    {SITUACOES_DO_MODULO.map((item) => (
                                                        <ToggleButton key={item.value} value={item.value} sx={{textTransform: 'none', px: 1.25}}>{item.label}</ToggleButton>
                                                    ))}
                                                </ToggleButtonGroup>
                                            ) : (
                                                <Chip size="small" variant={situacao === 'FORA' ? 'outlined' : 'filled'} color={COR[situacao]}
                                                      label={SITUACOES_DO_MODULO.find((item) => item.value === situacao)?.label} />
                                            )}
                                            {situacao === 'OPCIONAL' ? (
                                                <Box sx={{mt: 1}}>
                                                    {editavel ? (
                                                        <TextField size="small" type="number" value={preco} sx={{width: 170}}
                                                                   onChange={(event) => onPreco(modulo.id, event.target.value)}
                                                                   error={!(Number(preco) > 0)}
                                                                   helperText={Number(preco) > 0 ? 'por mês, além da mensalidade' : 'O opcional precisa de preço.'}
                                                                   inputProps={{min: 0, step: 10, 'aria-label': `Preço do opcional ${modulo.nome}`}}
                                                                   InputProps={{startAdornment: <InputAdornment position="start">+ R$</InputAdornment>}} />
                                                    ) : <Typography variant="body2">+ {formatReais(preco)} por mês</Typography>}
                                                </Box>
                                            ) : null}
                                        </Box>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}
